import React, { useState, useEffect, useRef } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Compass, MapPin, Plus, Edit, Trash2, Calendar, Clock, Route } from 'lucide-react';

const MissionPlanningPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const [missions, setMissions] = useState([
    {
      id: 1,
      name: 'Pacific Trench Mapping',
      status: 'active',
      progress: 67,
      startDate: '2025-01-02',
      endDate: '2025-01-15',
      waypoints: [
        { id: 1, name: 'Start Point', lat: -23.745, lon: -134.120, depth: 0 },
        { id: 2, name: 'Survey Area A', lat: -24.120, lon: -135.450, depth: 3840 },
        { id: 3, name: 'Survey Area B', lat: -24.890, lon: -136.220, depth: 4200 },
        { id: 4, name: 'End Point', lat: -25.340, lon: -137.100, depth: 4450 },
      ],
      distance: 487,
      eta: '2025-01-15 14:30',
    },
    {
      id: 2,
      name: 'Biodiversity Survey',
      status: 'planned',
      progress: 0,
      startDate: '2025-01-20',
      endDate: '2025-02-05',
      waypoints: [
        { id: 1, name: 'Launch', lat: -22.450, lon: -133.890, depth: 0 },
        { id: 2, name: 'Coral Reef Zone', lat: -23.120, lon: -134.670, depth: 120 },
      ],
      distance: 215,
      eta: '2025-02-05 09:00',
    },
  ]);

  const [selectedMission, setSelectedMission] = useState(missions[0]);
  const routeMapRef = useRef(null);

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  useEffect(() => {
    const canvas = routeMapRef.current;
    if (!canvas || !selectedMission) return;

    const ctx = canvas.getContext('2d');
    const canvasWidth = canvas.offsetWidth || canvas.parentElement?.offsetWidth || 800;
    const width = canvas.width = canvasWidth * 2;
    const height = canvas.height = 600;
    ctx.scale(2, 2);

    const centerX = width / 4;
    const centerY = height / 4;
    const radius = Math.min(width, height) / 4.5;

    // Clear canvas
    ctx.clearRect(0, 0, width / 2, height / 2);

    // Background
    const bgGradient = ctx.createRadialGradient(centerX, centerY - 50, 0, centerX, centerY, radius * 1.5);
    bgGradient.addColorStop(0, 'rgba(55, 211, 255, 0.08)');
    bgGradient.addColorStop(1, 'transparent');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(0, 0, width / 2, height / 2);

    // Ocean sphere with realistic gradient
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    const oceanGradient = ctx.createRadialGradient(
      centerX - radius * 0.4,
      centerY - radius * 0.4,
      radius * 0.1,
      centerX,
      centerY,
      radius
    );
    oceanGradient.addColorStop(0, 'rgba(100, 200, 230, 0.3)');
    oceanGradient.addColorStop(0.3, 'rgba(50, 150, 200, 0.25)');
    oceanGradient.addColorStop(0.6, 'rgba(30, 100, 150, 0.2)');
    oceanGradient.addColorStop(1, 'rgba(10, 40, 80, 0.9)');
    ctx.fillStyle = oceanGradient;
    ctx.fill();

    ctx.strokeStyle = 'rgba(55, 211, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.stroke();

    // Grid lines
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      const y = centerY + (i * radius) / 3;
      const ellipseHeight = Math.sqrt(Math.max(0, radius * radius - ((i * radius) / 3) ** 2));
      ctx.ellipse(centerX, y, ellipseHeight, ellipseHeight / 4, 0, 0, Math.PI * 2);
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Draw simplified continents (visible in Pacific region)
    // The waypoints are around lat -24, lon -135 (South Pacific)
    // Draw relative landmasses that would be visible
    
    const drawSimplifiedLand = (relativeCoords, offsetX = 0, offsetY = 0) => {
      ctx.beginPath();
      ctx.fillStyle = 'rgba(80, 120, 80, 0.3)';
      ctx.strokeStyle = 'rgba(120, 160, 120, 0.6)';
      ctx.lineWidth = 1.5;
      
      relativeCoords.forEach((point, idx) => {
        const x = centerX + point[0] + offsetX;
        const y = centerY + point[1] + offsetY;
        
        if (idx === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });
      
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    };

    // Draw some visible landmasses around the area
    // Easter Island (small dot to the east)
    ctx.beginPath();
    ctx.arc(centerX + radius * 0.6, centerY, radius * 0.05, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(80, 120, 80, 0.4)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(120, 160, 120, 0.6)';
    ctx.lineWidth = 1;
    ctx.stroke();
    
    // South American coast (western edge)
    drawSimplifiedLand([
      [-radius * 0.8, radius * 0.6],
      [-radius * 0.7, radius * 0.7],
      [-radius * 0.5, radius * 0.7],
      [-radius * 0.3, radius * 0.6],
      [-radius * 0.2, radius * 0.4],
      [-radius * 0.3, radius * 0.2],
      [-radius * 0.5, radius * 0.3],
      [-radius * 0.7, radius * 0.4],
      [-radius * 0.8, radius * 0.6]
    ]);
    
    // Australian coast hint (western edge, far left)
    drawSimplifiedLand([
      [radius * 0.6, -radius * 0.3],
      [radius * 0.7, -radius * 0.2],
      [radius * 0.8, -radius * 0.1],
      [radius * 0.8, radius * 0.1],
      [radius * 0.7, radius * 0.2],
      [radius * 0.6, radius * 0.1],
      [radius * 0.6, -radius * 0.3]
    ]);

    // Draw waypoints and route
    if (selectedMission.waypoints.length > 0) {
      const waypoints = selectedMission.waypoints.map(wp => ({
        x: centerX + (wp.lon + 134) * (radius / 50),
        y: centerY - (wp.lat + 24) * (radius / 50),
        name: wp.name,
      }));

      // Draw route lines with better visibility
      ctx.beginPath();
      ctx.moveTo(waypoints[0].x, waypoints[0].y);
      for (let i = 1; i < waypoints.length; i++) {
        ctx.lineTo(waypoints[i].x, waypoints[i].y);
      }
      ctx.strokeStyle = 'rgba(255, 215, 0, 0.8)'; // Gold color for better visibility
      ctx.lineWidth = 4;
      ctx.setLineDash([8, 8]);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw waypoint markers with enhanced visibility
      waypoints.forEach((wp, idx) => {
        const isStart = idx === 0;
        const isEnd = idx === waypoints.length - 1;
        
        // Outer glow (larger)
        const outerGlow = ctx.createRadialGradient(wp.x, wp.y, 0, wp.x, wp.y, 25);
        outerGlow.addColorStop(0, isStart ? 'rgba(16, 185, 129, 0.6)' : isEnd ? 'rgba(239, 68, 68, 0.6)' : 'rgba(255, 215, 0, 0.6)');
        outerGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = outerGlow;
        ctx.beginPath();
        ctx.arc(wp.x, wp.y, 25, 0, Math.PI * 2);
        ctx.fill();

        // Middle glow
        const middleGlow = ctx.createRadialGradient(wp.x, wp.y, 0, wp.x, wp.y, 15);
        middleGlow.addColorStop(0, isStart ? 'rgba(16, 185, 129, 0.8)' : isEnd ? 'rgba(239, 68, 68, 0.8)' : 'rgba(255, 215, 0, 0.8)');
        middleGlow.addColorStop(1, 'transparent');
        ctx.fillStyle = middleGlow;
        ctx.beginPath();
        ctx.arc(wp.x, wp.y, 15, 0, Math.PI * 2);
        ctx.fill();

        // Outer ring
        ctx.beginPath();
        ctx.arc(wp.x, wp.y, 12, 0, Math.PI * 2);
        ctx.strokeStyle = isStart ? 'rgba(16, 185, 129, 1)' : isEnd ? 'rgba(239, 68, 68, 1)' : 'rgba(255, 215, 0, 1)';
        ctx.lineWidth = 3;
        ctx.stroke();
        
        // Inner circle (marker)
        ctx.beginPath();
        ctx.arc(wp.x, wp.y, 9, 0, Math.PI * 2);
        ctx.fillStyle = isStart ? '#10b981' : isEnd ? '#ef4444' : '#ffd700';
        ctx.fill();

        // Label number with background
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.beginPath();
        ctx.arc(wp.x, wp.y, 6.5, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.fillStyle = '#ffffff';
        ctx.font = 'bold 10px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(idx + 1, wp.x, wp.y);
        
        // Waypoint name label below marker
        ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
        ctx.fillRect(wp.x - 30, wp.y + 18, 60, 14);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(wp.name.substring(0, 10), wp.x, wp.y + 20);
      });
    }
  }, [selectedMission]);

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="mission-planning-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/missions" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Compass className="h-6 w-6 text-primary" />
              <h1 className="text-2xl font-bold">Mission Planning & Management</h1>
            </div>
            <Button size="sm" data-testid="create-mission-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Mission
            </Button>
          </div>
          <p className="text-muted-foreground">Create, edit, and manage research missions with waypoint planning</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="mission-planning-page-grid">
          {/* Mission List */}
          <Card className="col-span-12 xl:col-span-4" data-testid="mission-list-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Route className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Active Missions</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {missions.map((mission) => (
                <div
                  key={mission.id}
                  className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                    selectedMission?.id === mission.id
                      ? 'bg-primary/10 border-primary/40'
                      : 'bg-secondary/20 border-border/40 hover:bg-secondary/40'
                  }`}
                  onClick={() => setSelectedMission(mission)}
                  data-testid={`mission-item-${mission.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <h4 className="font-medium" data-testid={`mission-name-${mission.id}`}>{mission.name}</h4>
                    <Badge 
                      variant={mission.status === 'active' ? 'default' : 'outline'}
                      data-testid={`mission-status-${mission.id}`}
                    >
                      {mission.status}
                    </Badge>
                  </div>
                  <div className="space-y-2 text-xs text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-3 w-3" />
                      <span>{mission.startDate} - {mission.endDate}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="h-3 w-3" />
                      <span>{mission.waypoints.length} waypoints • {mission.distance} nm</span>
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-mono">{mission.progress}%</span>
                    </div>
                    <Progress value={mission.progress} data-testid={`mission-progress-${mission.id}`} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Route Map */}
          <div 
            className="col-span-12 xl:col-span-8 rounded-lg overflow-hidden border border-border/70 bg-[radial-gradient(100%_140%_at_50%_0%,rgba(55,211,255,0.06),transparent_60%),hsl(var(--card))] shadow-ambient"
            style={{ height: 'min(60vh, 700px)' }}
            data-testid="route-map-container"
          >
            <div className="p-4 border-b border-border/40 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-primary" />
                <h3 className="font-medium">Mission Route</h3>
              </div>
              <Badge variant="outline" data-testid="route-map-badge">
                {selectedMission?.waypoints.length} waypoints
              </Badge>
            </div>
            <canvas 
              ref={routeMapRef} 
              className="w-full h-full"
              data-testid="route-map-canvas"
              role="img"
              aria-label={`Mission route map showing ${selectedMission?.waypoints.length} waypoints for ${selectedMission?.name}`}
            />
          </div>

          {/* Waypoint Details */}
          {selectedMission && (
            <Card className="col-span-12" data-testid="waypoint-details-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Waypoints</span>
                  </div>
                  <Button variant="outline" size="sm" data-testid="add-waypoint-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Waypoint
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {selectedMission.waypoints.map((waypoint, idx) => (
                    <div 
                      key={waypoint.id}
                      className="flex items-center justify-between p-4 rounded-lg bg-secondary/20 border border-border/40"
                      data-testid={`waypoint-${waypoint.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/20 text-primary font-mono text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <h4 className="font-medium mb-1" data-testid={`waypoint-name-${waypoint.id}`}>{waypoint.name}</h4>
                          <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono">
                            <span>LAT {waypoint.lat.toFixed(3)}°</span>
                            <span>LON {waypoint.lon.toFixed(3)}°</span>
                            <span>DEPTH {waypoint.depth}m</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" data-testid={`edit-waypoint-${waypoint.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" data-testid={`delete-waypoint-${waypoint.id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Mission Timeline */}
          {selectedMission && (
            <Card className="col-span-12" data-testid="mission-timeline-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Mission Timeline</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between py-3 border-b border-border/30">
                    <span className="text-sm text-muted-foreground">Start Date</span>
                    <span className="font-mono">{selectedMission.startDate}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border/30">
                    <span className="text-sm text-muted-foreground">End Date</span>
                    <span className="font-mono">{selectedMission.endDate}</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border/30">
                    <span className="text-sm text-muted-foreground">Total Distance</span>
                    <span className="font-mono">{selectedMission.distance} nautical miles</span>
                  </div>
                  <div className="flex items-center justify-between py-3 border-b border-border/30">
                    <span className="text-sm text-muted-foreground">Estimated Arrival</span>
                    <span className="font-mono">{selectedMission.eta}</span>
                  </div>
                  <div className="flex items-center justify-between py-3">
                    <span className="text-sm text-muted-foreground">Progress</span>
                    <div className="flex items-center gap-3">
                      <Progress value={selectedMission.progress} className="w-32" />
                      <span className="font-mono text-sm">{selectedMission.progress}%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default MissionPlanningPage;
